import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import {
  collection,
  doc,
  getDocs,
  updateDoc,
  setDoc,
  Timestamp,
} from "firebase/firestore";
import { v4 as uuidv4 } from "uuid"; // for unique offer IDs
import Cache from "../utils/cache";
import Select from "react-select";

const MAX_OFFERS_PER_DOC = 1000;

export default function AddWebsiteOffer() {
  const [vendor, setVendor] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [file, setFile] = useState(null);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [vendors, setVendors] = useState([]);

  // Load vendors for dropdown
  useEffect(() => {
    const fetchVendors = async () => {
      let vendorList = [];

      // Check cache
      const cached = Cache.getData();
      const cachedVendors = cached?.vendors;

      if (
        cachedVendors &&
        Array.isArray(cachedVendors) &&
        cachedVendors.length > 0 &&
        typeof cachedVendors[0] === "object"
      ) {
        vendorList = cachedVendors
          .map((v) => v.vendorName || v.id)
          .filter(Boolean);
      } else {
        try {
          const snapshot = await getDocs(collection(db, "vendors"));
          const groupedDocs = snapshot.docs.filter((doc) =>
            doc.id.startsWith("vendors_")
          );

          const allVendors = [];
          groupedDocs.forEach((doc) => {
            const data = doc.data();
            if (Array.isArray(data.vendors)) {
              data.vendors.forEach((vendor) => {
                if (!vendor.vendorName && vendor.id) {
                  vendor.vendorName = vendor.id;
                }
                allVendors.push(vendor);
              });
            }
          });

          vendorList = allVendors
            .map((v) => v.vendorName || v.id)
            .filter(Boolean);

          // Cache vendor data
          Cache.setData({
            ...cached,
            vendors: allVendors,
          });
        } catch (error) {
          console.error("Error loading vendors:", error);
        }
      }

      setVendors(
        vendorList
          .sort((a, b) => a.localeCompare(b))
          .map((v) => ({ label: v, value: v }))
      );
    };

    fetchVendors();
  }, []);


  // Handle file input change
  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setMessage("");
  };

  const createOfferObj = (offer) => ({
    offerId: uuidv4(),
    vendor: offer.vendor.trim(),
    promoCode: offer.promoCode ? offer.promoCode.trim() : null,
    description: offer.description.trim(),
    startDate: Timestamp.fromDate(new Date(offer.startDateTime || offer.startDate || startDate)),
    endDate: Timestamp.fromDate(new Date(offer.endDateTime || offer.endDate || endDate)),
    uploadedAt: Timestamp.now(),
  });

  // Common submit handler for both manual and file upload
  const handleUpload = async (e) => {
    e.preventDefault();
    setMessage("");

    if (!file && !description.trim()) {
      setMessage("Please fill the manual form or upload a JSON file.");
      return;
    }
    if (file && description.trim()) {
      setMessage("Please either fill the form or upload a JSON file, not both.");
      return;
    }

    setLoading(true);

    try {
      const websiteOffersCol = collection(db, "website_offers");
      const lastDocSnap = await getDocs(websiteOffersCol);
      let lastDoc = null;
      if (lastDocSnap.docs.length > 0) {
        // sort docs in memory by document ID descending (simulate orderBy "__name__" desc)
        const sortedDocs = lastDocSnap.docs.sort((a, b) => b.id.localeCompare(a.id));
        lastDoc = sortedDocs[0];
      }


      if (file) {
        if (file.type !== "application/json") {
          setMessage("Please upload a valid JSON file.");
          setLoading(false);
          return;
        }

        const text = await file.text();
        const data = JSON.parse(text);

        if (!Array.isArray(data)) {
          setMessage("JSON must be an array of offer objects.");
          setLoading(false);
          return;
        }

        let successCount = 0;
        const failedVendors = [];
        const offersToAdd = [];

        for (const offer of data) {
          if (!offer.vendor || !offer.description || !(offer.startDateTime || offer.startDate) || !(offer.endDateTime || offer.endDate)) {
            failedVendors.push(offer.vendor || "Unknown Vendor");
            continue;
          }
          offersToAdd.push(createOfferObj(offer));
        }

        if (offersToAdd.length === 0) {
          setMessage("No valid offers found in the JSON.");
          setLoading(false);
          return;
        }

        if (!lastDoc) {
          const newDocRef = doc(websiteOffersCol);
          await setDoc(newDocRef, {
            offers: offersToAdd,
            offerCount: offersToAdd.length,
          });
          successCount = offersToAdd.length;
        } else {
          const lastDocData = lastDoc.data();
          let currentOffers = Array.isArray(lastDocData.offers) ? lastDocData.offers : [];
          let currentCount = lastDocData.offerCount || currentOffers.length;

          for (const offer of offersToAdd) {
            if (currentCount < MAX_OFFERS_PER_DOC) {
              currentOffers.push(offer);
              currentCount++;
              successCount++;
            } else {
              // create new doc for remaining offers
              const newDocRef = doc(websiteOffersCol);
              const remainingOffers = offersToAdd.slice(successCount);
              await setDoc(newDocRef, {
                offers: remainingOffers,
                offerCount: remainingOffers.length,
              });
              break;
            }
          }

          await updateDoc(lastDoc.ref, {
            offers: currentOffers,
            offerCount: currentCount,
          });
        }

        setMessage(
          `Bulk upload complete: ${successCount} offers uploaded.` +
          (failedVendors.length > 0
            ? ` Failed for vendors: ${[...new Set(failedVendors)].join(", ")}.`
            : "")
        );
        setFile(null);
      } else {
        if (!vendor.trim()) {
          setMessage("Please select a vendor.");
          setLoading(false);
          return;
        }
        if (!startDate) {
          setMessage("Please select the start date.");
          setLoading(false);
          return;
        }
        if (!endDate) {
          setMessage("Please select the end date.");
          setLoading(false);
          return;
        }
        if (new Date(endDate) < new Date(startDate)) {
          setMessage("End date cannot be before start date.");
          setLoading(false);
          return;
        }

        const offerToAdd = createOfferObj({
          vendor,
          promoCode,
          description,
          startDate,
          endDate,
        });

        if (!lastDoc) {
          const newDocRef = doc(websiteOffersCol);
          await setDoc(newDocRef, {
            offers: [offerToAdd],
            offerCount: 1,
          });
        } else {
          const lastDocData = lastDoc.data();
          let currentOffers = Array.isArray(lastDocData.offers) ? lastDocData.offers : [];
          let currentCount = lastDocData.offerCount || currentOffers.length;

          if (currentCount < MAX_OFFERS_PER_DOC) {
            currentOffers.push(offerToAdd);
            currentCount++;
            await updateDoc(lastDoc.ref, {
              offers: currentOffers,
              offerCount: currentCount,
            });
          } else {
            const newDocRef = doc(websiteOffersCol);
            await setDoc(newDocRef, {
              offers: [offerToAdd],
              offerCount: 1,
            });
          }
        }

        setMessage("Website offer uploaded successfully!");
        setPromoCode("");
        setDescription("");
        setStartDate("");
        setEndDate("");
      }
    } catch (error) {
      setMessage("Error uploading website offer: " + error.message);
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="max-w-md mx-auto p-6 bg-white rounded shadow">
      <h2 className="text-2xl font-semibold mb-4 text-center">Upload Website Offer</h2>

      <form onSubmit={handleUpload} className="flex flex-col gap-4">
        {/* Manual Entry */}
        <label className="flex flex-col">
          Vendor:
          <Select
            name="vendor"
            isDisabled={loading || !!file}
            options={vendors}
            value={vendors.find((v) => v.value === vendor) || null}
            onChange={(selected) => setVendor(selected?.value || "")}
            placeholder="Select Vendor"
            isClearable
            className="mt-1"
          />
        </label>

        <label className="flex flex-col">
          Promo Code (Optional):
          <input
            type="text"
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value)}
            className="border rounded p-2 mt-1"
            placeholder="e.g. SAVE20"
            disabled={!!file}
          />
        </label>

        <label className="flex flex-col">
          Description:
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="border rounded p-2 mt-1"
            placeholder="Offer description"
            rows={3}
            disabled={!!file}
          />
        </label>

        <label className="flex flex-col">
          Start Date:
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="border rounded p-2 mt-1"
            disabled={!!file}
            required={!file}
          />
        </label>

        <label className="flex flex-col">
          End Date:
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="border rounded p-2 mt-1"
            disabled={!!file}
            required={!file}
          />
        </label>

        {/* OR separator */}
        <div className="text-center font-semibold my-2">OR</div>

        {/* File Upload */}
        <label className="flex flex-col items-center">
          Upload JSON File (Array of Offers):
          <input
            type="file"
            accept=".json,application/json"
            onChange={handleFileChange}
            disabled={loading}
          />
          {file && (
            <p className="mt-1 text-sm text-gray-600">{file.name}</p>
          )}
        </label>

        <button
          type="submit"
          disabled={loading}
          className="bg-indigo-600 text-white py-2 rounded hover:bg-indigo-700 disabled:opacity-50 transition"
        >
          {loading ? "Uploading..." : "Upload Website Offer"}
        </button>
      </form>

      {message && (
        <p className="mt-4 text-center text-sm text-red-600 whitespace-pre-line">
          {message}
        </p>
      )}
    </div>
  );
}
